import { Controller } from "@hotwired/stimulus"
import moment from 'moment'
import 'moment/locale/pt-br'

export default class extends Controller {
  static targets = ["wrapper", "loading", "searchInput", "pagination"]
  static values = { 
    apiurl: String,
    autoLoad: { type: Boolean, default: false }
  }
  loadedData = null
  params = null

  async connect() {
    moment.locale('pt-br')

    // Guarda o skeleton original para restaurá-lo num retry após erro
    // (showError substitui o conteúdo do loading target).
    if (this.hasLoadingTarget) {
      this.skeletonHtml = this.loadingTarget.innerHTML
    }

    this.loadData()

    if(this.hasSearchInputTarget) {
      this.setInputInitialValue()
    }
  }

  async loadData({name = '', search = false} = {}) {
    this.showLoading()
    this.params = new URLSearchParams();
    const urlParams = new URLSearchParams(window.location.search);

    if(name.length > 0 && search == true) {
      this.params.append("cnomefal", name)
    } else if (search == false) {
      if (urlParams.get("cnomefal")) {
        this.params.append("cnomefal", urlParams.get("cnomefal"))
      }
    }
    if(search === true) {
      const newUrl = `${window.location.pathname}${this.params.toString() ? "?" + (this.params.toString()) : ""}`;
      window.history.replaceState({}, '', newUrl);
      this.params.set("npagina", 1)
    } else {
      this.params.append("npagina", urlParams.get("npagina") || 1)
    }
    
    try {
      const response = await fetch(
        `${this.apiurlValue}?cdataini=20250101&ntamanho=10&${this.params.toString()}`, // FIX
      {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()

      this.loadedData = data

      this.renderData(data.result)

      if(this.hasPaginationTarget) {
        this.createPagination()
      }

      this.hideLoading()
    } catch (error) {
      console.error('Erro ao carregar dados:', error)
      this.showError()
    }
  }

  search(event) {
    event.preventDefault()
    const name = this.searchInputTarget.value.trim()
    this.loadData({name: name, search: true})
  }

  setInputInitialValue() {
    this.searchInputTarget.value = this.params.get("cnomefal") || ""
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      // Restaura o skeleton caso o conteúdo tenha sido trocado pelo card de erro
      if (this.skeletonHtml) {
        this.loadingTarget.innerHTML = this.skeletonHtml
      }
      this.loadingTarget.classList.remove('hidden')
    }

    this.wrapperTarget.innerHTML = ''
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add('hidden')
    }
  }

  renderData(items) {
    this.wrapperTarget.innerHTML = ''
    
    if (!items || items.length === 0) {
      this.wrapperTarget.innerHTML = `
        <div class="swiper-slide bg-white rounded-lg p-4">
          <p class="text-gray-500 text-center">Nenhum item encontrado</p>
        </div>
      `
      return
    }

    const slides = items.map(item => this.createSlide(item)).join('')
    this.wrapperTarget.innerHTML = slides
  }

  createSlide(item) {
    return `
      <div class="swiper-slide pb-10 px-1 sm:px-2">
        <div class="flex flex-col items-center justify-between obituary-item bg-white rounded-lg px-3 sm:px-6 lg:px-10 py-6 shadow-sm h-[270px] w-full max-w-full">
          ${item.nome_falecido ? `<p class="font-site text-gray-900 text-lg font-medium text-center px-1">${this.escapeHtml(item.nome_falecido)}</p>` : ''}
          
          <div class="flex flex-col items-center text-center px-1 mb-1">
            ${item.sala_velorio ? 
              `<div class="font-site text-gray-900">
                <p class="font-semibold mb-1">Velório - ${this.escapeHtml(item.sala_velorio)}</p>
                <p>Início: ${this.escapeHtml(this.formatDate(item.data_inicio_velorio))} às ${this.escapeHtml(item.hora_inicio_velorio)}</p>
                <p>Fim: ${this.escapeHtml(this.formatDate(item.data_fim_velorio))} às ${this.escapeHtml(item.hora_fim_velorio)}</p>
              </div>` : '<p class="font-site text-gray-900">Velado em outro local.</p>'
            }
          </div>
          
          <div class="flex flex-col items-center">
            <p class="font-site text-black font-semibold">
              ☆ ${this.escapeHtml(this.formatDate(item.data_nascimento))}
            </p>
            <p class="font-site text-black font-semibold">
              ✞ ${this.escapeHtml(this.formatDate(item.data_obito))}
            </p>
          </div>

          <p class="font-site text-gray-900 text-center px-1">
            ${item.tipo_servico === "SEPULTAMENTO" ? "Sepultamento" : "Cremação"} dia ${this.escapeHtml(this.formatDate(item.data_servico))} às ${this.escapeHtml(item.horario_servico)}
          </p>
        </div>
      </div>
    `
  }

  createPagination() {
    let pages_buttons = ""
    const FIRST_PAGE = 1
    const MAX_RANGE_TO_FIRST_PAGE = 3
    const FINAL_PAGE = this.loadedData.total_paginas

    if (this.loadedData.total_paginas <= 7) {
      const params = new URLSearchParams(this.params.toString());
      pages_buttons = 
        Array.from({length: this.loadedData.total_paginas}, (_, i) => i + 1).map((num) => {
          params.set("npagina", num)
          return (
            `
              <a class="inline-block py-1 px-3 border-1 border-solid cursor-pointer rounded ${this.params.get("npagina") == num ? "text-white bg-lime-600" : ""}">
                ${num}
              </a>
            `
          )
        }).join("")
    } else if (this.loadedData.total_paginas > 7) {
      const params = new URLSearchParams(this.params.toString());
      const actualPage = parseInt(this.params.get("npagina"));

      if(actualPage - FIRST_PAGE <= MAX_RANGE_TO_FIRST_PAGE) {
        for(let i = 1; i <= 5; i++) {
          params.set("npagina", i)
          pages_buttons +=
            `
              <a href="/obituarios${params ? "?" + params : ""}" class="inline-block py-1 px-3 border-1 border-solid cursor-pointer rounded ${actualPage == i ? "text-white bg-lime-600" : ""}">
                ${i}
              </a>
            `
        }
        pages_buttons +=
        `
          <div class="px-3">
            ...
          </div>
        `
        params.set("npagina", FINAL_PAGE)
        pages_buttons +=
        `
          <a href="/obituarios${params ? "?" + params : ""}" class="inline-block py-1 px-3 border-1 border-solid cursor-pointer rounded">
            ${FINAL_PAGE}
          </a>
        `
      } else {
        params.set("npagina", 1)
        pages_buttons += 
        `
          <a href="/obituarios${params ? "?" + params : ""}" class="inline-block py-1 px-3 border-1 border-solid cursor-pointer rounded">
            1
          </a>
        `
        pages_buttons +=
        `
          <div class="px-3">
            ...
          </div>
        `
        if(FINAL_PAGE - actualPage > 3) {
          for(let i = (actualPage - 1); i <= (actualPage + 1); i++) {
            params.set("npagina", i)
            pages_buttons +=
              `
                <a href="/obituarios${params ? "?" + params : ""}" class="inline-block py-1 px-3 border-1 border-solid cursor-pointer rounded ${actualPage == i ? "text-white bg-lime-600" : ""}">
                  ${i}
                </a>
              `
          }
          pages_buttons +=
          `
            <div class="px-3">
              ...
            </div>
          `
          params.set("npagina", FINAL_PAGE)
          pages_buttons +=
          `
            <a href="/obituarios${params ? "?" + params : ""}" class="inline-block py-1 px-3 border-1 border-solid cursor-pointer rounded">
              ${FINAL_PAGE}
            </a>
          `
        } else {
          const subtract = 5 - ((FINAL_PAGE - actualPage) + 1)
          for(let i = (actualPage - subtract); i <= FINAL_PAGE; i++) {
            params.set("npagina", i)
            pages_buttons +=
              `
                <a href="/obituarios${params ? "?" + params : ""}" class="inline-block py-1 px-3 border-1 border-solid cursor-pointer rounded ${actualPage == i ? "text-white bg-lime-600" : ""}">
                  ${i}
                </a>
              `
          }
        }
      }
    }

    this.paginationTarget.innerHTML = `
      ${pages_buttons}
    `
  }

  formatDate(dataString) {
    const dataMoment = moment(dataString, 'YYYYMMDD').locale('pt-br')
    
    return dataMoment.format('LL')
  }

  // A mensagem técnica fica só no console (já logada no catch); o visitante vê
  // um aviso acolhedor com caminho alternativo (telefone 24h) e botão de retry.
  // Renderiza no loading target (fora do swiper-wrapper), senão o swiper
  // trata o card como slide e ele fica com 1/3 da largura, colado à esquerda.
  showError() {
    this.wrapperTarget.innerHTML = ''

    const errorHtml = `
      <div class="w-full px-1 sm:px-2 pb-10">
        <div class="flex flex-col items-center justify-center gap-3 bg-white rounded-lg shadow-sm h-[270px] max-w-md mx-auto px-8 text-center">
          <svg class="w-8 h-8 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
          </svg>
          <p class="font-site text-gray-800 font-medium">Não foi possível carregar os obituários agora</p>
          <p class="font-site text-sm text-gray-500">
            Tente novamente em instantes ou fale com nosso atendimento 24h:
            <a href="tel:+556240060033" class="font-semibold text-blue-700 whitespace-nowrap">(62) 4006-0033</a>
          </p>
          <button
            data-action="click->obituary#loadData"
            class="mt-1 px-5 py-2 bg-lime-600 text-white rounded-lg hover:bg-lime-700 transition-colors cursor-pointer"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    `

    if (this.hasLoadingTarget) {
      this.loadingTarget.innerHTML = errorHtml
      this.loadingTarget.classList.remove('hidden')
    } else {
      this.wrapperTarget.innerHTML = errorHtml
    }
  }

  refresh() {
    this.loadData()
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}